import { defineSlotRecipe } from '@chakra-ui/react';
import { Radii, Colors, FontSizes, Space, FontWeights, Shadows } from '../../types';

export const tabsSlotRecipe = defineSlotRecipe({
  slots: ['root', 'list', 'trigger', 'content', 'contentGroup', 'indicator'],
  base: {
    root: {
      position: 'relative',
    },
    content: {
      pt: Space.SM,
      // The panel fills the row; that pressure is what wraps a vertical list's long labels.
      _vertical: { width: '100%' },
      // Stock pads only the top under this nested selector, which beats a flat rule, so the 16px inset lives here.
      _horizontal: { px: Space.SPACE_4, pb: Space.SPACE_4 },
    },
    list: {
      // Horizontal non-fitted case; vertical and fitted unset this in compoundVariants.
      overflowX: 'auto',
      boxSizing: 'border-box',
      scrollbarWidth: 'thin',
      overflowY: 'visible',
      isolation: 'isolate',
    },
    trigger: {
      fontSize: FontSizes.MD,
      fontWeight: FontWeights.MEDIUM,
      lineHeight: 'shorter',
      // Stock's trigger pins `height: var(--tabs-height)`, which quantizes every trigger
      // and clips wrapped labels.
      height: 'auto',
      maxWidth: { base: '200px', md: 'initial' },
      textWrap: 'balance',
      gap: Space.XS,
      alignItems: 'center',
      whiteSpace: 'normal',
      hyphens: 'auto',
      textAlign: 'start',
      position: 'relative',
      '& .svg-inline--fa': {
        alignSelf: 'baseline',
        color: Colors.ICON_DEFAULT,
      },
      _hover: {
        bg: Colors.SURFACE_HOVER,
      },
      _selected: {
        color: Colors.TEXT_EMPHASIS,
      },
    },
  },
  variants: {
    // Chakra's own `fitted` variant, re-declared empty so the compoundVariants entry
    // below can reference the key; the built-in's styles merge in unchanged.
    fitted: { true: {} },
    size: {
      // Chakra's built-in sizes set `trigger.textStyle` ('sm' below `lg`), a compound token that expands later
      // and overrides our plain `fontSize` whatever the merge order; `textStyle: 'unset'` drops it.
      sm: {
        trigger: {
          textStyle: 'unset',
          fontSize: FontSizes.SM,
          py: Space['2XS'],
          px: Space.XS,
        },
      },
      md: {
        trigger: {
          textStyle: 'unset',
          fontSize: FontSizes.MD,
          py: Space.SM,
          px: Space.SM,
        },
      },
      lg: {
        trigger: {
          textStyle: 'unset',
          fontSize: FontSizes.LG,
          py: Space.SM,
          px: Space.MD,
        },
      },
    },
    orientation: {
      vertical: {
        trigger: {
          justifyContent: 'flex-start',
          py: Space.XS,
          px: Space.MD,
        },
      },
      horizontal: {
        trigger: {
          py: Space.SM,
          px: Space.SM,
        },
      },
    },
    variant: {
      line: {
        list: {
          m: 0,
          borderWidth: 0,
          boxShadow: 'inset 0 -1px 0 0 var(--udl-colors-border-subdued)',
          // Stock sets the `line.list` edge under `_horizontal`/`_vertical`, which outranks the flat zero above, so
          // the zero is restated there; our inset box-shadow is the edge, down the inline end when vertical.
          _horizontal: { borderBottomWidth: 0 },
          _vertical: {
            borderEndWidth: 0,
            boxShadow: 'inset -1px 0 0 0 var(--udl-colors-border-subdued)',
          },
        },
        trigger: {
          m: 0,
          borderWidth: 0,
          // The 3px edge swaps by orientation (`borderEndWidth` when vertical); both spellings sit inside the
          // orientation selector so neither leaks across the axis.
          _horizontal: { borderBottomWidth: '3px' },
          _vertical: { borderBottomWidth: 0, borderEndWidth: '3px' },
          borderColor: Colors.TRANSPARENT,
          // Stock's `line` variant sets `color: 'fg.muted'`, and a variant layer beats
          // `base` — so the inherited ink is re-stated here, in the same winning layer.
          color: Colors.TEXT_EMPHASIS,
          _active: { bg: 'unset' },
          _selected: {
            '--tabs-color': `colors.${Colors.TEXT_BRAND}`,
            borderColor: 'var(--tabs-color)',
            color: Colors.TEXT_EMPHASIS,
            _dark: {
              '--tabs-color': `colors.${Colors.TEXT_BRAND}`,
              color: 'var(--tabs-color)',
            },
            // Stock `line` draws a selected `::before` bar from `--indicator-color` under `[data-orientation]`,
            // so both axes blank it at that nesting level, or it paints a second bar over our edge.
            _horizontal: {
              '--indicator-color': 'transparent',
              '--indicator-color-fallback': 'transparent',
            },
            _vertical: {
              '--indicator-color': 'transparent',
              '--indicator-color-fallback': 'transparent',
            },
          },
        },
      },
      pill: {
        list: { gap: Space['2XS'] },
        trigger: {
          py: Space.XS,
          px: Space.SM,
          rounded: Radii.LG,
          _selected: { bg: Colors.SURFACE_ACTIVE },
        },
      },
      segmented: {
        list: {
          bg: Colors.SURFACE_NEUTRAL_SUBDUED,
          rounded: 'calc(var(--udl-radii-lg) + 2px)',
          p: '0.5',
          w: 'fit-content',
        },
        trigger: {
          rounded: Radii.LG,
          _selected: { bg: Colors.SURFACE_ELEVATED, shadow: Shadows.XS },
        },
      },
      enclosed: {
        list: {
          // A flat full-width strip, where stock's enclosed list is a filled segmented group.
          display: 'flex',
          bg: Colors.TRANSPARENT,
          padding: 0,
          borderRadius: 0,
          m: 0,
          borderWidth: 0,
          boxShadow: 'inset 0 -1px 0 0 var(--udl-colors-border-default)',
          _vertical: { boxShadow: 'inset -1px 0 0 0 var(--udl-colors-border-default)' },
        },
        trigger: {
          borderWidth: '1px',
          borderColor: Colors.TRANSPARENT,
          borderRadius: Radii.LG,
          mb: 0,
          // The corners that meet the panel are squared and that edge blends into it; both sit inside the
          // orientation selector, which stock also uses here.
          _horizontal: { borderEndStartRadius: 0, borderEndEndRadius: 0 },
          _vertical: {
            borderStartEndRadius: 0,
            borderEndEndRadius: 0,
            me: 0,
            textAlign: 'center',
            justifyContent: 'center',
          },
          _selected: {
            shadow: Shadows.NONE,
            borderColor: Colors.BORDER_DEFAULT,
            bg: Colors.SURFACE_PAGE,
            color: Colors.TEXT_EMPHASIS,
            _horizontal: { borderBottomColor: Colors.SURFACE_PAGE },
            _vertical: { borderInlineEndColor: Colors.SURFACE_PAGE },
          },
        },
      },
    },
  },
  // A single variant group cannot express a condition spanning both `fitted` and
  // `orientation`, so those combinations live here.
  compoundVariants: [
    { variant: 'segmented', fitted: true, css: { list: { w: 'full' } } },
    // Fitted tabs divide the full row, so they must neither be width-capped nor scroll —
    // at mobile widths they grow taller instead, keeping every label visible.
    {
      fitted: true,
      css: {
        trigger: { maxWidth: { base: 'unset', md: 'initial' } },
        list: { overflowX: 'visible' },
      },
    },
    // A column of tabs has nothing to scroll sideways.
    { orientation: 'vertical', css: { list: { overflowX: 'visible' } } },
    // Small horizontal line tabs pad 4px by 16px: `orientation.horizontal`'s padding otherwise outranks every
    // size, and stock's list `minH: var(--tabs-height)` stretches the triggers to 36px.
    {
      variant: 'line',
      size: 'sm',
      orientation: 'horizontal',
      css: { trigger: { py: Space.SPACE_1, px: Space.SPACE_4 }, list: { minH: 'auto' } },
    },
  ],
  defaultVariants: {
    orientation: 'horizontal',
    size: 'md',
  },
});
